import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { currentMonthStr } from '../lib/format';

/**
 * Selection state shared by every Profit IQ tab: the date window, the month the
 * cost editors write to, and the data source.
 *
 * The mobile app keeps this in Redux so it survives the drawer unmounting the
 * screen. Nothing outside this section reads it, so here it is section-scoped
 * context — the same reasoning that keeps Inventory Management's server state
 * out of the global store.
 *
 * `dataMode` is the exception that is persisted: it is a per-device preference,
 * not a business setting, and the source a browser reads has to survive a
 * reload. The mobile app writes it to AsyncStorage; this writes localStorage.
 */

const DATA_MODE_KEY = 'profitIqDataMode';

function loadDataMode() {
  try {
    return localStorage.getItem(DATA_MODE_KEY) === 'csv' ? 'csv' : 'online';
  } catch {
    return 'online';
  }
}

function saveDataMode(mode) {
  try {
    localStorage.setItem(DATA_MODE_KEY, mode);
  } catch {
    /* storage unavailable (private mode); the selection stays in memory */
  }
}

const ProfitIqContext = createContext(null);

export function ProfitIqProvider({ children }) {
  const [range, setRange] = useState('thismonth');
  const [customDays] = useState(30);
  const [customLabel] = useState('Custom Range');
  const [month, setMonthState] = useState(currentMonthStr);
  const [costTarget, setCostTarget] = useState('month');
  const [dataMode, setDataModeState] = useState(loadDataMode);

  // Insight drill-down lives here rather than in the Insights screen so it
  // survives a trip to another tab and back.
  const [insightTopic, setInsightTopic] = useState(null);
  const [insightQ, setInsightQ] = useState(null);

  /**
   * Set by an edit, cleared once the dashboard reads have been refreshed.
   *
   * Rebuilding the dashboard costs three calls out to MMS — the catalog, its
   * categories and the inventory unit costs — on top of the order
   * aggregations, so an edit no longer triggers it. The section refreshes when
   * another tab is opened instead, which is when the figures are next read.
   */
  const [dashboardStale, setDashboardStale] = useState(false);
  const markDashboardStale = useCallback(() => setDashboardStale(true), []);
  const clearDashboardStale = useCallback(() => setDashboardStale(false), []);

  const setMonth = useCallback((next) => {
    setMonthState(next);
    // Choosing a month means editing that month, never the template.
    setCostTarget('month');
  }, []);

  const setDataMode = useCallback((next) => {
    setDataModeState(next);
    saveDataMode(next);
  }, []);

  const value = useMemo(
    () => ({
      range,
      setRange,
      customDays,
      customLabel,
      month,
      setMonth,
      costTarget,
      setCostTarget,
      dataMode,
      setDataMode,
      insightTopic,
      setInsightTopic,
      insightQ,
      setInsightQ,
      dashboardStale,
      markDashboardStale,
      clearDashboardStale,
    }),
    [
      range,
      customDays,
      customLabel,
      month,
      setMonth,
      costTarget,
      dataMode,
      setDataMode,
      insightTopic,
      insightQ,
      dashboardStale,
      markDashboardStale,
      clearDashboardStale,
    ]
  );

  return (
    <ProfitIqContext.Provider value={value}>{children}</ProfitIqContext.Provider>
  );
}

export function useProfitIqContext() {
  const ctx = useContext(ProfitIqContext);
  if (!ctx) {
    throw new Error('useProfitIqContext must be used inside ProfitIqProvider');
  }
  return ctx;
}

export default ProfitIqContext;
